/**
 * Cookie utility functions
 */

/**
 * Get a cookie value (raw, without consent check)
 * @param {string} name - Cookie name
 * @returns {string|null}
 */
const getCookieRaw = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for(let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * Check if user has given consent for functional cookies
 * @returns {boolean}
 */
export const hasFunctionalConsent = () => {
  const consent = getCookieRaw('cookie_consent');
  
  if (!consent) {
    return false;
  }
  
  try {
    const decoded = decodeURIComponent(consent);
    const prefs = JSON.parse(decoded);
    return prefs.functional === true;
  } catch (error) {
    console.error('Error parsing consent:', error);
    return false;
  }
};

/**
 * Set a cookie
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Expiration in days
 * @param {boolean} skipConsentCheck - Skip consent check (for consent cookie itself)
 */
export const setCookie = (name, value, days = 365, skipConsentCheck = false) => {
  // Always allow setting the consent cookie itself
  if (name === 'cookie_consent' || skipConsentCheck) {
    try {
      const d = new Date();
      d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
      const expires = "expires=" + d.toUTCString();
      
      // Use SameSite=Lax for better compatibility (Strict would block some legitimate uses)
      // Don't use Secure flag in development (localhost is http, not https)
      const isSecure = window.location.protocol === 'https:';
      const secureFlag = isSecure ? ';Secure' : '';
      const cookieString = `${name}=${value};${expires};path=/;SameSite=Lax${secureFlag}`;
      
      document.cookie = cookieString;
      
      // Verify it was set immediately
      const verify = getCookieRaw(name);
      if (!verify) {
        console.error(`Cookie NOT saved: ${name}. Check browser settings.`);
        return false;
      }
      return true;
    } catch (error) {
      console.error(`Error setting cookie ${name}:`, error);
      return false;
    }
  }
  
  // For functional cookies, check consent
  if (!hasFunctionalConsent()) {
    return false;
  }
  
  try {
    const d = new Date();
    d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = "expires=" + d.toUTCString();
    
    const isSecure = window.location.protocol === 'https:';
    const secureFlag = isSecure ? ';Secure' : '';
    const cookieString = `${name}=${value};${expires};path=/;SameSite=Lax${secureFlag}`;
    
    document.cookie = cookieString;
    
    // Verify
    const verify = getCookieRaw(name);
    return verify !== null;
  } catch (error) {
    console.error(`Error setting functional cookie ${name}:`, error);
    return false;
  }
};

/**
 * Get a cookie value
 * @param {string} name - Cookie name
 * @returns {string|null} - Cookie value or null if not found
 */
export const getCookie = (name) => {
  return getCookieRaw(name);
};

/**
 * Delete a cookie
 * @param {string} name - Cookie name
 */
export const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

/**
 * Check if user has already viewed an announcement
 * @param {number} announcementId - Announcement ID
 * @returns {boolean} - True if already viewed
 */
export const hasViewedAnnouncement = (announcementId) => {
  const viewedAnnouncements = getCookie('viewed_announcements');
  
  if (!viewedAnnouncements) {
    return false;
  }
  
  try {
    const viewedIds = JSON.parse(decodeURIComponent(viewedAnnouncements));
    
    // Convert to number for comparison (in case it's stored as string)
    const numId = Number(announcementId);
    return viewedIds.includes(numId) || viewedIds.includes(String(numId)) || viewedIds.includes(announcementId);
  } catch (error) {
    console.error('Error parsing viewed announcements:', error);
    return false;
  }
};

/**
 * Mark an announcement as viewed
 * @param {number} announcementId - Announcement ID
 */
export const markAnnouncementAsViewed = (announcementId) => {
  const viewedAnnouncements = getCookie('viewed_announcements');
  let viewedIds = [];
  
  if (viewedAnnouncements) {
    try {
      viewedIds = JSON.parse(decodeURIComponent(viewedAnnouncements));
    } catch (error) {
      console.error('Error parsing viewed announcements:', error);
      viewedIds = [];
    }
  }
  
  if (!viewedIds.includes(announcementId)) {
    viewedIds.push(announcementId);
    
    // Keep only last 1000 viewed announcements to prevent cookie from getting too large
    if (viewedIds.length > 1000) {
      viewedIds = viewedIds.slice(-1000);
    }
    
    const encoded = encodeURIComponent(JSON.stringify(viewedIds));
    setCookie('viewed_announcements', encoded, 365); // Store for 1 year
  }
};

/**
 * Get user's preferred language from cookie
 * @returns {string} - Language code ('en' or 'ro')
 */
export const getLanguagePreference = () => {
  return getCookie('user_language') || 'ro';
};

/**
 * Set user's preferred language in cookie
 * @param {string} language - Language code ('en' or 'ro')
 */
export const setLanguagePreference = (language) => {
  setCookie('user_language', language, 365); // Store for 1 year
};
